import { Button, Group, Modal, Stack, Text } from "@mantine/core"
import { upperFirst } from "@mantine/hooks"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { queryClient } from "~/backend/queryClient"
import { findViewsQueryKey, useDeleteView } from "~/gen/api"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type DeleteViewModalProps = {
  opened: boolean
  onClose: () => void
  viewId: string
  tableContext: string
  viewName: string
}

export const DeleteViewModal = ({
  opened,
  onClose,
  viewId,
  tableContext,
  viewName,
}: DeleteViewModalProps) => {
  const { t } = useTranslation()
  const [, setSearchParams] = useSearchParams()
  const { mutate: deleteView } = useDeleteView()

  const handleSubmit = () => {
    deleteView(
      { id: viewId },
      {
        onSuccess: () => {
          setSearchParams((prev) => {
            prev.delete("viewId")
            return prev
          })
          queryClient.invalidateQueries({
            queryKey: findViewsQueryKey({
              tableContext,
            }),
          })
          showSuccessNotification({
            title: t("table.views.delete.success.title"),
            message: t("table.views.delete.success.message"),
          })
          onClose()
        },
        onError: (error: AxiosError) => {
          showAxiosError({
            error,
            showNotification: true,
            defaultMessage: t("table.views.delete.error.message"),
            notificationTitle: t("table.views.delete.error.title"),
          })
        },
      }
    )

    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("table.views.delete.modal.title")}
    >
      <Stack>
        <Text>{t("table.views.delete.modal.description", { viewName })}</Text>
        <Stack>
          <Group grow>
            <Button fullWidth onClick={onClose} variant="secondary">
              {upperFirst(t("table.views.delete.modal.cancel"))}
            </Button>
            <Button fullWidth onClick={handleSubmit} variant="primary">
              {upperFirst(t("table.views.delete.modal.delete"))}
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Modal>
  )
}
